import { X, Flame, ThermometerSun, Snowflake, Mail, Phone, MapPin, Calendar, Clock, User, MessageSquare, Briefcase, Target } from 'lucide-react';
import type { Lead } from './LeadCard';

interface LeadViewModalProps {
  lead: Lead;
  onClose: () => void;
  onEdit: (lead: Lead) => void;
  getAssigneeName?: (userId: string | null) => string | null;
}

const scoreConfig = {
  hot: {
    icon: Flame,
    label: 'Hot',
    colors: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
  },
  warm: {
    icon: ThermometerSun,
    label: 'Warm',
    colors: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  },
  cold: {
    icon: Snowflake,
    label: 'Cold',
    colors: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  },
};

const statusLabels: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  demo_scheduled: 'Demo Scheduled',
  demo_done: 'Demo Done',
  converted: 'Converted',
  lost: 'Lost',
};

const statusColors: Record<string, string> = {
  new: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  contacted: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  demo_scheduled: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  demo_done: 'bg-sage/10 text-sage',
  converted: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  lost: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
};

const sourceLabels: Record<string, string> = {
  reddit: 'Reddit',
  instagram: 'Instagram',
  google: 'Google',
  referral: 'Referral',
  cold_outreach: 'Cold Outreach',
  other: 'Other',
};

function formatDate(date: string | null): string {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(date: string | null): string {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export default function LeadViewModal({ lead, onClose, onEdit, getAssigneeName }: LeadViewModalProps) {
  const config = scoreConfig[lead.score];
  const ScoreIcon = config.icon;

  const location = [lead.city, lead.state, lead.country].filter(Boolean).join(', ');
  const fullAddress = lead.address ? `${lead.address}${location ? ', ' + location : ''}` : location;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-stone-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-stone-100 dark:bg-stone-800 rounded-full flex items-center justify-center text-stone-600 dark:text-stone-300">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                {lead.contact_name}
              </h3>
              {lead.studio_name && (
                <p className="text-sm text-stone-500 dark:text-stone-400">{lead.studio_name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(lead)}
              className="px-4 py-2 text-sm font-medium text-sage hover:bg-sage/10 rounded-lg transition-colors"
            >
              Edit
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Status Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border ${config.colors}`}>
              <ScoreIcon className="w-4 h-4" />
              {config.label}
            </span>
            <span className={`text-sm font-medium px-3 py-1.5 rounded-full ${statusColors[lead.status]}`}>
              {statusLabels[lead.status]}
            </span>
            {lead.is_qualified === 1 && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                Qualified
              </span>
            )}
            {lead.is_decision_maker === 1 && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                Decision Maker
              </span>
            )}
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <Mail className="w-5 h-5 text-stone-400" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Email</p>
                {lead.email ? (
                  <a href={`mailto:${lead.email}`} className="text-sm text-stone-700 dark:text-stone-300 hover:text-sage">
                    {lead.email}
                  </a>
                ) : (
                  <p className="text-sm text-stone-400 dark:text-stone-500">—</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <Phone className="w-5 h-5 text-stone-400" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Phone</p>
                {lead.phone ? (
                  <a href={`tel:${lead.phone}`} className="text-sm text-stone-700 dark:text-stone-300 hover:text-sage">
                    {lead.phone}
                  </a>
                ) : (
                  <p className="text-sm text-stone-400 dark:text-stone-500">—</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl md:col-span-2">
              <MapPin className="w-5 h-5 text-stone-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Location</p>
                <p className="text-sm text-stone-700 dark:text-stone-300">{fullAddress || '—'}</p>
              </div>
            </div>
          </div>

          {/* Lead Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <Target className="w-5 h-5 text-stone-400" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Source</p>
                <p className="text-sm text-stone-700 dark:text-stone-300">
                  {lead.source ? (sourceLabels[lead.source] || lead.source) : '—'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <Briefcase className="w-5 h-5 text-stone-400" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Current Software</p>
                <p className="text-sm text-stone-700 dark:text-stone-300">{lead.current_software || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <User className="w-5 h-5 text-indigo-500" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Assigned To</p>
                <p className={`text-sm font-medium ${lead.assigned_to && getAssigneeName ? 'text-indigo-600 dark:text-indigo-400' : 'text-stone-400 dark:text-stone-500'}`}>
                  {lead.assigned_to && getAssigneeName ? (getAssigneeName(lead.assigned_to) || 'Unknown') : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Pain Point */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Pain Point
            </h4>
            <p className={`text-sm bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 ${lead.pain_point ? 'text-stone-600 dark:text-stone-400' : 'text-stone-400 dark:text-stone-500'}`}>
              {lead.pain_point || '—'}
            </p>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Notes
            </h4>
            <p className={`text-sm bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 whitespace-pre-wrap ${lead.notes ? 'text-stone-600 dark:text-stone-400' : 'text-stone-400 dark:text-stone-500'}`}>
              {lead.notes || '—'}
            </p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className={`flex items-center gap-3 p-3 rounded-xl ${lead.demo_date ? 'bg-sage/10' : 'bg-stone-50 dark:bg-stone-800/50'}`}>
              <Calendar className={`w-5 h-5 ${lead.demo_date ? 'text-sage' : 'text-stone-400'}`} />
              <div>
                <p className={`text-xs ${lead.demo_date ? 'text-sage/70' : 'text-stone-500 dark:text-stone-400'}`}>Demo Scheduled</p>
                <p className={`text-sm font-medium ${lead.demo_date ? 'text-sage' : 'text-stone-400 dark:text-stone-500'}`}>
                  {lead.demo_date ? (
                    <>
                      {formatDate(lead.demo_date)}
                      {lead.demo_time && ` at ${lead.demo_time}`}
                    </>
                  ) : '—'}
                </p>
              </div>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-xl ${lead.next_follow_up_date ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-stone-50 dark:bg-stone-800/50'}`}>
              <Clock className={`w-5 h-5 ${lead.next_follow_up_date ? 'text-amber-600 dark:text-amber-400' : 'text-stone-400'}`} />
              <div>
                <p className={`text-xs ${lead.next_follow_up_date ? 'text-amber-600/70 dark:text-amber-400/70' : 'text-stone-500 dark:text-stone-400'}`}>Next Follow-up</p>
                <p className={`text-sm font-medium ${lead.next_follow_up_date ? 'text-amber-600 dark:text-amber-400' : 'text-stone-400 dark:text-stone-500'}`}>
                  {lead.next_follow_up_date ? formatDate(lead.next_follow_up_date) : '—'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <Clock className="w-5 h-5 text-stone-400" />
              <div>
                <p className="text-xs text-stone-500 dark:text-stone-400">Last Contact</p>
                <p className={`text-sm ${lead.last_contact_date ? 'text-stone-700 dark:text-stone-300' : 'text-stone-400 dark:text-stone-500'}`}>
                  {lead.last_contact_date ? formatDate(lead.last_contact_date) : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="flex flex-wrap gap-4 text-xs text-stone-500 dark:text-stone-400 border-t border-stone-200 dark:border-stone-800 pt-4">
            <span>Created: {formatDateTime(lead.created_at)}</span>
            <span>Updated: {formatDateTime(lead.updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
